import { useState } from "react";
import {
  useStacks as usePulumiStacks,
  useInfrastructureSummary,
} from "../hooks/useInfrastructure";
import PageHeader from "../components/common/PageHeader";
import StatusBadge from "../components/common/StatusBadge";
import MetricCard from "../components/common/MetricCard";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorBanner from "../components/common/ErrorBanner";
import SearchInput from "../components/common/SearchInput";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { format } from "date-fns";

const PROVIDER_COLORS: Record<string, string> = {
  aws: "#FF9900",
  azure: "#0078D4",
  gcp: "#4285F4",
};

const statusMap: Record<string, string> = {
  succeeded: "green",
  failed: "red",
  "in progress": "yellow",
};

export default function Infrastructure() {
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("all");
  const {
    data: stacks,
    isLoading,
    error,
    refetch,
  } = usePulumiStacks();
  const { data: summary } = useInfrastructureSummary();

  const filteredStacks = (stacks ?? []).filter((s) => {
    const matchesSearch =
      !search ||
      s.pulumiStackName.toLowerCase().includes(search.toLowerCase()) ||
      s.projectName.toLowerCase().includes(search.toLowerCase()) ||
      (s.serviceSlug ?? "").toLowerCase().includes(search.toLowerCase());
    const matchesProvider =
      providerFilter === "all" || s.cloudProvider === providerFilter;
    return matchesSearch && matchesProvider;
  });

  const providerPieData = Object.entries(
    summary?.resourcesByProvider ?? {}
  ).map(([name, value]) => ({ name: name.toUpperCase(), value }));

  if (isLoading) return <LoadingSpinner message="Loading infrastructure..." />;
  if (error)
    return (
      <ErrorBanner message="Failed to load infrastructure" onRetry={refetch} />
    );

  return (
    <div className="animate-fade-in space-y-6">
      <PageHeader
        title="Infrastructure"
        description="Pulumi stacks and cloud resources across all providers"
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetricCard
          label="Total Stacks"
          value={summary?.totalStacks ?? 0}
        />
        <MetricCard
          label="Total Resources"
          value={summary?.totalResources ?? 0}
        />
        <MetricCard
          label="AWS Resources"
          value={summary?.resourcesByProvider?.aws ?? 0}
        />
        <MetricCard
          label="Azure Resources"
          value={summary?.resourcesByProvider?.azure ?? 0}
        />
      </div>

      {/* Resources by Provider Chart + Stacks by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card">
          <div className="card-header">
            <h3 className="text-card-title text-slate-900 dark:text-white">
              Resources by Provider
            </h3>
          </div>
          <div className="card-body">
            {providerPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={providerPieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {providerPieData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={
                          PROVIDER_COLORS[entry.name.toLowerCase()] ?? "#8884d8"
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400 dark:text-slate-500">
                No resource data available
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="text-card-title text-slate-900 dark:text-white">
              Stacks by Status
            </h3>
          </div>
          <div className="card-body space-y-3">
            {Object.entries(summary?.stacksByStatus ?? {}).map(
              ([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between p-3 rounded-lg bg-surface-50 dark:bg-slate-800/50"
                >
                  <StatusBadge
                    status={statusMap[status.toLowerCase()] ?? "gray"}
                    label={status}
                  />
                  <span className="text-lg font-semibold text-slate-900 dark:text-white">
                    {count}
                  </span>
                </div>
              )
            )}
            {Object.keys(summary?.stacksByStatus ?? {}).length === 0 && (
              <div className="text-center text-slate-400 dark:text-slate-500 py-8">
                No stack data
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stacks Table */}
      <div className="card">
        <div className="card-header flex flex-wrap items-center gap-4">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search stacks..."
          />
          <select
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            className="select-field"
          >
            <option value="all">All Providers</option>
            <option value="aws">AWS</option>
            <option value="azure">Azure</option>
            <option value="gcp">GCP</option>
          </select>
        </div>
        <div className="card-body p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-50 dark:bg-slate-800/50 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              <tr>
                <th className="px-4 py-3">Stack</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3">Provider</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Resources</th>
                <th className="px-4 py-3">Last Updated</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredStacks.map((stack) => (
                <tr
                  key={stack.id}
                  className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors"
                >
                  <td className="px-4 py-3">
                    <a
                      href={stack.consoleUrl ?? "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 font-medium"
                    >
                      {stack.projectName}/{stack.stackName}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {stack.serviceSlug ?? "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                      style={{
                        backgroundColor: `${PROVIDER_COLORS[stack.cloudProvider] ?? "#6366f1"}20`,
                        color: PROVIDER_COLORS[stack.cloudProvider] ?? "#6366f1",
                      }}
                    >
                      {stack.cloudProvider.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={
                        statusMap[
                          (stack.lastUpdateStatus ?? "").toLowerCase()
                        ] ?? "gray"
                      }
                      label={stack.lastUpdateStatus ?? "Unknown"}
                    />
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                    {stack.resourceCount}
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-500 text-xs">
                    {stack.lastUpdatedAt
                      ? format(
                          new Date(stack.lastUpdatedAt),
                          "MMM d, HH:mm"
                        )
                      : "—"}
                  </td>
                </tr>
              ))}
              {filteredStacks.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-slate-400 dark:text-slate-500"
                  >
                    No stacks found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
